import {
  createUnvalidatedValidationSummary,
  type CoasterProject,
  type ModularTrackSectionGeometry,
  type TrackBehavior,
  type TrackEndpointPose,
  type TrackNode,
  type TrackPieceDefinitionId,
  type TrackPieceParameters,
  type Vec3,
} from '../schema/index.ts';
import { add, length, normalize, scale, sub } from '../track/vector.ts';
import { newStableId, type TrackOperationResult } from './operations.ts';

export const MODULAR_PITCH_STATES = [-90, -60, -45, -30, 0, 30, 45, 60, 90] as const;
export const MODULAR_BANK_STATES = [-60, -45, -30, 0, 30, 45, 60] as const;
export const MODULAR_HEADING_STEP_DEGREES = 45;
export const MODULAR_ELEVATION_STEP_METERS = 4;

export type ModularPitchDegrees = (typeof MODULAR_PITCH_STATES)[number];
export type ModularBankDegrees = (typeof MODULAR_BANK_STATES)[number];
export type ModularHeadingStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type ModularPieceDefinitionId =
  | 'station'
  | 'straight'
  | 'turn_left'
  | 'turn_right'
  | 'slope_up'
  | 'slope_down'
  | 'bank_left'
  | 'bank_right'
  | 'bank_level'
  | 'lift'
  | 'launch'
  | 'brake'
  | 'photo'
  | 'loop_left'
  | 'loop_right'
  | 'roll_left'
  | 'roll_right'
  | 'corkscrew_left'
  | 'corkscrew_right';

export type ModularTrackShapeId = Exclude<
  ModularPieceDefinitionId,
  'station' | 'lift' | 'launch' | 'brake' | 'photo'
>;

export type TrackType = 'normal' | 'lift' | 'launch' | 'brake' | 'photo';

export interface ModularPieceSegment {
  readonly end: TrackNode;
  readonly geometry: ModularTrackSectionGeometry;
  readonly behavior: TrackBehavior;
}

export interface ModularPieceGeometry {
  readonly definitionId: ModularPieceDefinitionId;
  readonly definitionVersion: 2;
  readonly parameters: TrackPieceParameters;
  readonly entryPose: TrackEndpointPose;
  readonly exitPose: TrackEndpointPose;
  readonly segments: readonly ModularPieceSegment[];
}

export type ModularPieceResult =
  | { readonly ok: true; readonly geometry: ModularPieceGeometry }
  | { readonly ok: false; readonly code: string; readonly message: string };

export interface ModularPieceAuditRecord {
  readonly definitionId: ModularPieceDefinitionId;
  readonly handedness: 'left' | 'right' | 'none';
  readonly entryPose: TrackEndpointPose;
  readonly exitPose: TrackEndpointPose;
  readonly sectionCount: number;
  readonly centerlineLengthMeters: number;
  readonly elevationChangeMeters: number;
  readonly headingChangeDegrees: number;
  readonly bankChangeDegrees: number;
  readonly windingChangeDegrees: number;
  readonly startTangentMismatchDegrees: number;
  readonly endTangentMismatchDegrees: number;
}

const LENGTH_INDEX = { short: 0, medium: 1, long: 2 } as const;
const LEVEL_LENGTHS = [8, 16, 24] as const;
const SLOPE_ELEVATION_CHANGES = [4, 8, 12] as const;
const TURN_RADII = { small: 8, medium: 12, large: 16 } as const;

function canonicalNumber(value: number): number {
  const rounded = Math.round(value * 1_000) / 1_000;
  return Object.is(rounded, -0) ? 0 : rounded;
}

export function canonicalVec3(value: Vec3): Vec3 {
  return {
    x: canonicalNumber(value.x),
    y: canonicalNumber(value.y),
    z: canonicalNumber(value.z),
  };
}

function canonicalPose(value: TrackEndpointPose): TrackEndpointPose {
  return { ...value, position: canonicalVec3(value.position) };
}

export function modularPosesMatch(
  first: TrackEndpointPose,
  second: TrackEndpointPose,
  positionToleranceMeters = 0.001,
): boolean {
  return (
    length(sub(first.position, second.position)) <= positionToleranceMeters &&
    first.headingStep === second.headingStep &&
    first.pitchDegrees === second.pitchDegrees &&
    first.bankDegrees === second.bankDegrees
  );
}

export function headingRadians(step: number): number {
  return ((((step % 8) + 8) % 8) * Math.PI) / 4;
}

export function poseForward(pose: Pick<TrackEndpointPose, 'headingStep' | 'pitchDegrees'>): Vec3 {
  const heading = headingRadians(pose.headingStep);
  const pitch = (pose.pitchDegrees * Math.PI) / 180;
  const horizontal = Math.cos(pitch);
  return normalize({
    x: Math.cos(heading) * horizontal,
    y: Math.sin(pitch),
    z: Math.sin(heading) * horizontal,
  });
}

export function poseLeft(pose: Pick<TrackEndpointPose, 'headingStep'>): Vec3 {
  const heading = headingRadians(pose.headingStep);
  return { x: -Math.sin(heading), y: 0, z: Math.cos(heading) };
}

function poseUp(): Vec3 {
  return { x: 0, y: 1, z: 0 };
}

export function trackBehaviorForType(type: TrackType): TrackBehavior {
  switch (type) {
    case 'lift':
      return { kind: 'lift', targetSpeedMps: 5 };
    case 'launch':
      return { kind: 'launch', targetSpeedMps: 20, accelerationMps2: 7 };
    case 'brake':
      return { kind: 'brake', targetSpeedMps: 9, decelerationMps2: 4 };
    case 'photo':
      return { kind: 'photo' };
    default:
      return { kind: 'normal' };
  }
}

function behaviorFor(id: ModularPieceDefinitionId): TrackBehavior {
  if (id === 'station') return { kind: 'station', holdSeconds: 1.5, dispatchSpeedMps: 5 };
  if (id === 'lift' || id === 'launch' || id === 'brake' || id === 'photo')
    return trackBehaviorForType(id);
  return trackBehaviorForType('normal');
}

export function legacyDefinitionToComposite(id: ModularPieceDefinitionId): {
  shapeId: ModularTrackShapeId;
  trackType: TrackType;
} {
  if (id === 'lift' || id === 'launch' || id === 'brake' || id === 'photo')
    return { shapeId: 'straight', trackType: id };
  return {
    shapeId: id === 'station' ? 'straight' : id,
    trackType: 'normal',
  };
}

export function trackTypeCompatibility(
  shapeId: ModularTrackShapeId,
  entry: TrackEndpointPose,
  trackType: TrackType,
  parameters: TrackPieceParameters = {},
): { readonly allowed: boolean; readonly reason: string | null } {
  if (trackType === 'normal') return { allowed: true, reason: null };
  if (entry.bankDegrees !== 0)
    return {
      allowed: false,
      reason: 'Return to Level Bank before adding a special Track Type.',
    };
  const isStraight = shapeId === 'straight';
  const isSlope = shapeId === 'slope_up' || shapeId === 'slope_down';
  const pitchIndex = MODULAR_PITCH_STATES.indexOf(entry.pitchDegrees as ModularPitchDegrees);
  const targetPitch =
    parameters.targetPitchDegrees ??
    (shapeId === 'slope_up'
      ? (MODULAR_PITCH_STATES[pitchIndex + 1] ?? entry.pitchDegrees)
      : shapeId === 'slope_down'
        ? (MODULAR_PITCH_STATES[pitchIndex - 1] ?? entry.pitchDegrees)
        : entry.pitchDegrees);
  if (trackType === 'brake' || trackType === 'photo') {
    if (!isStraight || entry.pitchDegrees !== 0)
      return {
        allowed: false,
        reason:
          trackType === 'brake'
            ? 'Brakes require a flat, unbanked Straight.'
            : 'Photo requires a flat, unbanked Straight.',
      };
    return { allowed: true, reason: null };
  }
  if (!isStraight && !isSlope)
    return {
      allowed: false,
      reason: `${trackType === 'lift' ? 'Lift' : 'Launch'} is available on Straight or Slope track.`,
    };
  if (entry.pitchDegrees < 0)
    return {
      allowed: false,
      reason: `${trackType === 'lift' ? 'Lift' : 'Launch'} cannot begin on descending track.`,
    };
  if (trackType === 'lift' && isSlope && entry.pitchDegrees === 0 && targetPitch < 0)
    return {
      allowed: false,
      reason: 'Lift can crest into a drop only after an upward track section.',
    };
  if (trackType === 'launch' && targetPitch < 0)
    return {
      allowed: false,
      reason: 'Launch cannot transition into descending track.',
    };
  return { allowed: true, reason: null };
}

function fail(code: string, message: string): ModularPieceResult {
  return { ok: false, code, message };
}

function straightDisplacement(pose: TrackEndpointPose, lengthName: 'short' | 'medium' | 'long') {
  const index = LENGTH_INDEX[lengthName];
  if (pose.pitchDegrees === 0) return scale(poseForward(pose), LEVEL_LENGTHS[index]);
  const elevation = Math.sign(pose.pitchDegrees) * SLOPE_ELEVATION_CHANGES[index];
  if (Math.abs(pose.pitchDegrees) === 90) return { x: 0, y: elevation, z: 0 };
  const pitch = (Math.abs(pose.pitchDegrees) * Math.PI) / 180;
  const horizontal = Math.abs(elevation) / Math.tan(pitch);
  const heading = headingRadians(pose.headingStep);
  return {
    x: Math.cos(heading) * horizontal,
    y: elevation,
    z: Math.sin(heading) * horizontal,
  };
}

function oneSegment(
  id: ModularPieceDefinitionId,
  entry: TrackEndpointPose,
  exit: TrackEndpointPose,
  control1: Vec3,
  control2: Vec3,
  parameters: TrackPieceParameters,
  behavior = behaviorFor(id),
): ModularPieceGeometry {
  return {
    definitionId: id,
    definitionVersion: 2,
    parameters: { ...parameters, definitionVersion: 2 },
    entryPose: canonicalPose(entry),
    exitPose: canonicalPose(exit),
    segments: [
      {
        end: {
          id: newStableId('node'),
          position: canonicalVec3(exit.position),
          bankDegrees: exit.bankDegrees,
          rollWindingDegrees: exit.rollWindingDegrees,
        },
        geometry: {
          kind: 'cubic_bezier',
          control1: canonicalVec3(control1),
          control2: canonicalVec3(control2),
        },
        behavior: structuredClone(behavior),
      },
    ],
  };
}

function straightPiece(
  id: ModularPieceDefinitionId,
  entry: TrackEndpointPose,
  parameters: TrackPieceParameters,
): ModularPieceGeometry {
  const lengthName = parameters.length ?? 'medium';
  const displacement = straightDisplacement(entry, lengthName);
  const exit = canonicalPose({ ...entry, position: add(entry.position, displacement) });
  return oneSegment(
    id,
    entry,
    exit,
    add(entry.position, scale(displacement, 1 / 3)),
    add(entry.position, scale(displacement, 2 / 3)),
    { ...parameters, length: lengthName },
  );
}

function turnPiece(
  id: 'turn_left' | 'turn_right',
  entry: TrackEndpointPose,
  parameters: TrackPieceParameters,
): ModularPieceResult {
  if (entry.pitchDegrees !== 0)
    return fail(
      'turn_requires_level',
      'Add slope pieces until the track is level before placing a turn.',
    );
  const turnDegrees = parameters.turnDegrees === 90 ? 90 : 45;
  const radiusName = parameters.radius ?? 'medium';
  const radius = TURN_RADII[radiusName];
  const direction = id === 'turn_left' ? 1 : -1;
  const angle = (turnDegrees * Math.PI) / 180;
  const forward = poseForward(entry);
  const left = poseLeft(entry);
  const endPosition = add(
    entry.position,
    add(
      scale(forward, radius * Math.sin(angle)),
      scale(left, direction * radius * (1 - Math.cos(angle))),
    ),
  );
  const exitHeading = (((entry.headingStep + direction * (turnDegrees / 45)) % 8) + 8) % 8;
  const exit = canonicalPose({
    ...entry,
    position: endPosition,
    headingStep: exitHeading as ModularHeadingStep,
  });
  const endForward = poseForward(exit);
  const handle = (4 / 3) * Math.tan(angle / 4) * radius;
  return {
    ok: true,
    geometry: oneSegment(
      id,
      entry,
      exit,
      add(entry.position, scale(forward, handle)),
      sub(exit.position, scale(endForward, handle)),
      {
        ...parameters,
        turnDegrees,
        radius: radiusName,
      },
    ),
  };
}

function slopePiece(
  id: 'slope_up' | 'slope_down',
  entry: TrackEndpointPose,
  parameters: TrackPieceParameters,
): ModularPieceResult {
  const current = entry.pitchDegrees as ModularPitchDegrees;
  const fallbackIndex = MODULAR_PITCH_STATES.indexOf(current) + (id === 'slope_up' ? 1 : -1);
  const target =
    (parameters.targetPitchDegrees as ModularPitchDegrees | undefined) ??
    MODULAR_PITCH_STATES[fallbackIndex];
  if (target === undefined)
    return fail('pitch_limit', 'The track is already at the supported pitch limit.');
  if (target === current)
    return fail('pitch_unchanged', 'Choose a different slope angle or add Straight track.');
  const angularChange = Math.abs(target - current);
  if (angularChange > 90)
    return fail('pitch_change_limit', 'Slope can change by at most 90° in one piece.');

  const averagePitch = ((current + target) * 0.5 * Math.PI) / 180;
  const horizontalRun = Math.max(8, Math.ceil(angularChange / 30) * 8);
  const rawElevation = Math.tan(averagePitch) * horizontalRun;
  const boundedElevation = Number.isFinite(rawElevation)
    ? Math.max(-horizontalRun * 2, Math.min(horizontalRun * 2, rawElevation))
    : Math.sign(current + target) * horizontalRun * 2;
  const elevation =
    Math.abs(boundedElevation) < 1
      ? 0
      : Math.round(boundedElevation / MODULAR_ELEVATION_STEP_METERS) *
        MODULAR_ELEVATION_STEP_METERS;
  const heading = headingRadians(entry.headingStep);
  const exit = canonicalPose({
    ...entry,
    position: {
      x: entry.position.x + Math.cos(heading) * horizontalRun,
      y: entry.position.y + elevation,
      z: entry.position.z + Math.sin(heading) * horizontalRun,
    },
    pitchDegrees: target,
  });
  const chordLength = Math.max(8, length(sub(exit.position, entry.position)));
  const handle = Math.max(4, chordLength * 0.42);
  const resolvedId = target > current ? 'slope_up' : 'slope_down';
  return {
    ok: true,
    geometry: oneSegment(
      resolvedId,
      entry,
      exit,
      add(entry.position, scale(poseForward(entry), handle)),
      sub(exit.position, scale(poseForward(exit), handle)),
      { ...parameters, targetPitchDegrees: target },
    ),
  };
}

function bankPiece(
  id: 'bank_left' | 'bank_right' | 'bank_level',
  entry: TrackEndpointPose,
  parameters: TrackPieceParameters,
): ModularPieceResult {
  const requested =
    parameters.targetBankDegrees ?? (id === 'bank_left' ? -30 : id === 'bank_right' ? 30 : 0);
  if (!MODULAR_BANK_STATES.includes(requested as ModularBankDegrees))
    return fail('unsupported_bank', 'Choose a supported bank angle.');
  if (requested === entry.bankDegrees)
    return fail('bank_unchanged', 'Choose a different bank angle or add Straight track.');
  if (Math.abs(requested - entry.bankDegrees) > 90)
    return fail('bank_change_limit', 'Bank can change by at most 90° in one piece.');
  const displacement = straightDisplacement(entry, 'short');
  const exit = canonicalPose({
    ...entry,
    position: add(entry.position, displacement),
    bankDegrees: requested as ModularBankDegrees,
  });
  return {
    ok: true,
    geometry: oneSegment(
      requested === 0 ? 'bank_level' : requested < entry.bankDegrees ? 'bank_left' : 'bank_right',
      entry,
      exit,
      add(entry.position, scale(displacement, 1 / 3)),
      add(entry.position, scale(displacement, 2 / 3)),
      { ...parameters, length: 'short', targetBankDegrees: requested },
    ),
  };
}

interface LocalCurvePoint {
  readonly forward: number;
  readonly side: number;
  readonly height: number;
  readonly winding: number;
}

function smootherStep(progress: number): number {
  return progress ** 3 * (progress * (progress * 6 - 15) + 10);
}

function fairingPulse(progress: number): number {
  return 256 * progress ** 4 * (1 - progress) ** 4;
}

function localInversionPoint(
  id:
    'loop_left' | 'loop_right' | 'roll_left' | 'roll_right' | 'corkscrew_left' | 'corkscrew_right',
  progress: number,
): LocalCurvePoint {
  const leftHanded = id.endsWith('_left');
  const sideDirection = leftHanded ? 1 : -1;
  const rollDirection = leftHanded ? -1 : 1;
  const phase = progress * Math.PI * 2;
  const envelope = Math.sin(Math.PI * progress) ** 2;
  if (id.startsWith('loop_')) {
    const chord = 24;
    const height = 28;
    const crownWeight = Math.sin(Math.PI * progress) ** 2;
    const amplitude = height * (0.55 + (0.42 - 0.55) * crownWeight);
    return {
      forward: chord * progress + amplitude * Math.sin(phase),
      side:
        sideDirection *
        (8 * smootherStep(progress) + 3 * envelope + 2 * Math.sin(phase) * envelope),
      height: (height * (1 - Math.cos(phase))) / 2,
      winding: 0,
    };
  }
  if (id.startsWith('roll_'))
    return {
      forward: 32 * progress,
      side: sideDirection * 1.6 * Math.sin(phase) * envelope,
      height: 2.2 * fairingPulse(progress),
      winding: rollDirection * 360 * smootherStep(progress),
    };
  const radius = 5.5;
  return {
    forward: 32 * progress,
    side: sideDirection * radius * envelope * Math.sin(phase),
    height: radius * envelope * (1 - Math.cos(phase)),
    winding: rollDirection * 360 * smootherStep(progress),
  };
}

function inversionPiece(
  id:
    'loop_left' | 'loop_right' | 'roll_left' | 'roll_right' | 'corkscrew_left' | 'corkscrew_right',
  entry: TrackEndpointPose,
  parameters: TrackPieceParameters,
): ModularPieceResult {
  if (entry.pitchDegrees !== 0 || entry.bankDegrees !== 0)
    return fail(
      'inversion_requires_level',
      'Use a level track and Level Bank before placing an inversion.',
    );
  const segmentCount = id.startsWith('loop_') ? 20 : id.startsWith('roll_') ? 12 : 16;
  const forward = poseForward(entry);
  const left = poseLeft(entry);
  const up = poseUp();
  const world = (point: LocalCurvePoint): Vec3 =>
    add(
      entry.position,
      add(scale(forward, point.forward), add(scale(left, point.side), scale(up, point.height))),
    );
  const pointAt = (progress: number) => localInversionPoint(id, Math.min(1, Math.max(0, progress)));
  const derivativeAt = (progress: number): Vec3 => {
    const epsilon = 0.0001;
    const before = world(pointAt(Math.max(0, progress - epsilon)));
    const after = world(pointAt(Math.min(1, progress + epsilon)));
    return scale(
      sub(after, before),
      1 / Math.max(epsilon, Math.min(1, progress + epsilon) - Math.max(0, progress - epsilon)),
    );
  };
  const segments: ModularPieceSegment[] = [];
  for (let index = 0; index < segmentCount; index += 1) {
    const startProgress = index / segmentCount;
    const endProgress = (index + 1) / segmentCount;
    const start = world(pointAt(startProgress));
    const endLocal = pointAt(endProgress);
    const end = world(endLocal);
    const delta = 1 / segmentCount;
    segments.push({
      end: {
        id: newStableId('node'),
        position: canonicalVec3(end),
        bankDegrees: 0,
        rollWindingDegrees: canonicalNumber(entry.rollWindingDegrees + endLocal.winding),
      },
      geometry: {
        kind: 'cubic_bezier',
        control1: canonicalVec3(add(start, scale(derivativeAt(startProgress), delta / 3))),
        control2: canonicalVec3(sub(end, scale(derivativeAt(endProgress), delta / 3))),
      },
      behavior: { kind: 'normal' },
    });
  }
  const finalPoint = pointAt(1);
  const exit = canonicalPose({
    ...entry,
    position: world(finalPoint),
    rollWindingDegrees: entry.rollWindingDegrees + finalPoint.winding,
  });
  return {
    ok: true,
    geometry: {
      definitionId: id,
      definitionVersion: 2,
      parameters: { ...parameters, definitionVersion: 2, size: parameters.size ?? 'medium' },
      entryPose: canonicalPose(entry),
      exitPose: exit,
      segments,
    },
  };
}

export function generateModularPiece(
  id: ModularPieceDefinitionId,
  entryInput: TrackEndpointPose,
  parameters: TrackPieceParameters = {},
): ModularPieceResult {
  const entry = canonicalPose(entryInput);
  if (id === 'station') {
    if (entry.pitchDegrees !== 0 || entry.bankDegrees !== 0)
      return fail('station_requires_level', 'Stations require level, unbanked track.');
    return { ok: true, geometry: straightPiece(id, entry, { ...parameters, length: 'medium' }) };
  }
  if (id === 'straight' || id === 'launch' || id === 'brake' || id === 'photo')
    return { ok: true, geometry: straightPiece(id, entry, parameters) };
  if (id === 'lift') {
    if (entry.pitchDegrees <= 0)
      return fail('lift_requires_climb', 'Use Slope Up before adding a lift section.');
    return { ok: true, geometry: straightPiece(id, entry, parameters) };
  }
  if (id === 'turn_left' || id === 'turn_right') return turnPiece(id, entry, parameters);
  if (id === 'slope_up' || id === 'slope_down') return slopePiece(id, entry, parameters);
  if (id === 'bank_left' || id === 'bank_right' || id === 'bank_level')
    return bankPiece(id, entry, parameters);
  return inversionPiece(id, entry, parameters);
}

export function generateCompositeModularPiece(
  shapeId: ModularTrackShapeId,
  entryInput: TrackEndpointPose,
  parameters: TrackPieceParameters = {},
  trackType: TrackType = 'normal',
): ModularPieceResult {
  const entry = canonicalPose(entryInput);
  const compatibility = trackTypeCompatibility(shapeId, entry, trackType, parameters);
  if (!compatibility.allowed)
    return fail('incompatible_track_type', compatibility.reason ?? 'Track Type is not available.');
  const generated = generateModularPiece(shapeId, entry, parameters);
  if (!generated.ok) return generated;
  return {
    ok: true,
    geometry: {
      ...generated.geometry,
      segments: generated.geometry.segments.map((segment) => ({
        ...segment,
        behavior: trackBehaviorForType(trackType),
      })),
    },
  };
}

export function modularEndPose(project: CoasterProject): TrackEndpointPose | null {
  if (
    project.track.geometryKind !== 'modular_piecewise_bezier' ||
    project.track.closed ||
    !project.editor.pieceGroups.length
  )
    return null;
  return project.editor.pieceGroups.at(-1)?.exitPose ?? null;
}

export function appendModularPiece(
  project: CoasterProject,
  id: ModularPieceDefinitionId,
  parameters: TrackPieceParameters = {},
): TrackOperationResult {
  if (project.track.geometryKind !== 'modular_piecewise_bezier')
    return {
      ok: false,
      code: 'legacy_geometry',
      message: 'This coaster uses legacy track geometry.',
    };
  if (project.track.closed)
    return { ok: false, code: 'already_closed', message: 'The circuit is already closed.' };
  const entry = modularEndPose(project);
  if (!entry) return { ok: false, code: 'missing_entry_pose', message: 'Place the station first.' };
  const generated = generateModularPiece(id, entry, parameters);
  if (!generated.ok) return generated;
  const next = structuredClone(project);
  const sectionIds: string[] = [];
  const stationEntry = next.editor.pieceGroups[0]?.entryPose ?? null;
  const closesCircuit =
    next.editor.pieceGroups.length >= 4 &&
    stationEntry !== null &&
    modularPosesMatch(generated.geometry.exitPose, stationEntry);
  for (let index = 0; index < generated.geometry.segments.length; index += 1) {
    const segment = generated.geometry.segments[index]!;
    const from = next.track.nodes.at(-1);
    if (!from)
      return { ok: false, code: 'missing_entry_node', message: 'Place the station first.' };
    const closesOnThisSegment = closesCircuit && index === generated.geometry.segments.length - 1;
    const endNode = closesOnThisSegment ? next.track.nodes[0]! : structuredClone(segment.end);
    if (!closesOnThisSegment) next.track.nodes.push(endNode);
    const sectionId = newStableId('section');
    next.track.sections.push({
      id: sectionId,
      fromNodeId: from.id,
      toNodeId: endNode.id,
      behavior: structuredClone(segment.behavior),
      geometry: structuredClone(segment.geometry),
    });
    sectionIds.push(sectionId);
  }
  next.editor.pieceGroups.push({
    id: newStableId('piece'),
    definitionId: id as TrackPieceDefinitionId,
    sectionIds,
    parameters: generated.geometry.parameters,
    definitionVersion: 2,
    entryPose: generated.geometry.entryPose,
    exitPose: generated.geometry.exitPose,
  });
  if (closesCircuit) next.track.closed = true;
  next.validation = createUnvalidatedValidationSummary();
  next.metadata.updatedAt = new Date().toISOString();
  return {
    ok: true,
    project: next,
    message: closesCircuit
      ? `${modularPieceLabel(id)} added. Circuit closed.`
      : `${modularPieceLabel(id)} added.`,
  };
}

export function appendCompositeModularPiece(
  project: CoasterProject,
  shapeId: ModularTrackShapeId,
  parameters: TrackPieceParameters = {},
  trackType: TrackType = 'normal',
): TrackOperationResult {
  if (project.track.geometryKind !== 'modular_piecewise_bezier')
    return {
      ok: false,
      code: 'legacy_geometry',
      message: 'This coaster uses legacy track geometry.',
    };
  if (project.track.closed)
    return { ok: false, code: 'already_closed', message: 'The circuit is already closed.' };
  const entry = modularEndPose(project);
  if (!entry) return { ok: false, code: 'missing_entry_pose', message: 'Place the station first.' };
  const generated = generateCompositeModularPiece(shapeId, entry, parameters, trackType);
  if (!generated.ok) return generated;
  const next = structuredClone(project);
  const sectionIds: string[] = [];
  const stationEntry = next.editor.pieceGroups[0]?.entryPose ?? null;
  const closesCircuit =
    next.editor.pieceGroups.length >= 4 &&
    stationEntry !== null &&
    modularPosesMatch(generated.geometry.exitPose, stationEntry);
  for (let index = 0; index < generated.geometry.segments.length; index += 1) {
    const segment = generated.geometry.segments[index]!;
    const from = next.track.nodes.at(-1);
    if (!from)
      return { ok: false, code: 'missing_entry_node', message: 'Place the station first.' };
    const closesOnThisSegment = closesCircuit && index === generated.geometry.segments.length - 1;
    const endNode = closesOnThisSegment ? next.track.nodes[0]! : structuredClone(segment.end);
    if (!closesOnThisSegment) next.track.nodes.push(endNode);
    const sectionId = newStableId('section');
    next.track.sections.push({
      id: sectionId,
      fromNodeId: from.id,
      toNodeId: endNode.id,
      behavior: structuredClone(segment.behavior),
      geometry: structuredClone(segment.geometry),
    });
    sectionIds.push(sectionId);
  }
  next.editor.pieceGroups.push({
    id: newStableId('piece'),
    definitionId: generated.geometry.definitionId as TrackPieceDefinitionId,
    sectionIds,
    parameters: generated.geometry.parameters,
    definitionVersion: 2,
    entryPose: generated.geometry.entryPose,
    exitPose: generated.geometry.exitPose,
  });
  if (closesCircuit) next.track.closed = true;
  next.validation = createUnvalidatedValidationSummary();
  next.metadata.updatedAt = new Date().toISOString();
  const typeLabel =
    trackType === 'normal'
      ? ''
      : ` · ${trackType === 'brake' ? 'Brakes' : trackType[0]!.toUpperCase() + trackType.slice(1)}`;
  return {
    ok: true,
    project: next,
    message: closesCircuit
      ? `${modularPieceLabel(shapeId)}${typeLabel} added. Circuit closed.`
      : `${modularPieceLabel(shapeId)}${typeLabel} added.`,
  };
}

export function removeLastModularPiece(project: CoasterProject): TrackOperationResult {
  if (project.track.geometryKind !== 'modular_piecewise_bezier')
    return {
      ok: false,
      code: 'legacy_geometry',
      message: 'This coaster does not use modular track.',
    };
  if (project.editor.pieceGroups.length <= 1)
    return {
      ok: false,
      code: 'station_only',
      message: 'The station is the only placed piece.',
    };
  const lastPiece = project.editor.pieceGroups.at(-1)!;
  const sectionIds = new Set(lastPiece.sectionIds);
  const suffix = project.track.sections.slice(-lastPiece.sectionIds.length);
  if (
    suffix.length !== lastPiece.sectionIds.length ||
    suffix.some((section, index) => section.id !== lastPiece.sectionIds[index])
  )
    return {
      ok: false,
      code: 'non_suffix_piece',
      message: 'The last modular piece is not a removable track suffix.',
    };

  const next = structuredClone(project);
  next.track.sections = next.track.sections.filter((section) => !sectionIds.has(section.id));
  next.editor.pieceGroups = next.editor.pieceGroups.slice(0, -1);
  const retainedNodeIds = new Set<string>();
  next.track.sections.forEach((section) => {
    retainedNodeIds.add(section.fromNodeId);
    retainedNodeIds.add(section.toNodeId);
  });
  if (next.track.nodes[0]) retainedNodeIds.add(next.track.nodes[0].id);
  next.track.nodes = next.track.nodes.filter((node) => retainedNodeIds.has(node.id));
  next.track.closed = false;
  next.validation = createUnvalidatedValidationSummary();
  next.metadata.updatedAt = new Date().toISOString();
  return {
    ok: true,
    project: next,
    message: `${modularPieceLabel(lastPiece.definitionId as ModularPieceDefinitionId)} removed.`,
  };
}

export function placeModularStation(
  project: CoasterProject,
  startPosition: Vec3,
  headingStep: 0 | 2 | 4 | 6 = 0,
): TrackOperationResult {
  if (project.track.geometryKind !== 'modular_piecewise_bezier' || project.track.nodes.length)
    return { ok: false, code: 'station_exists', message: 'This coaster already has a station.' };
  const entry: TrackEndpointPose = {
    position: canonicalVec3(startPosition),
    headingStep,
    pitchDegrees: 0,
    bankDegrees: 0,
    rollWindingDegrees: 0,
  };
  const generated = generateModularPiece('station', entry, { length: 'medium' });
  if (!generated.ok) return generated;
  const next = structuredClone(project);
  const firstNode: TrackNode = {
    id: newStableId('node'),
    position: entry.position,
    bankDegrees: 0,
    rollWindingDegrees: 0,
  };
  next.track.nodes.push(firstNode);
  const sectionIds: string[] = [];
  for (const segment of generated.geometry.segments) {
    const from = next.track.nodes.at(-1)!;
    next.track.nodes.push(structuredClone(segment.end));
    const sectionId = newStableId('section');
    next.track.sections.push({
      id: sectionId,
      fromNodeId: from.id,
      toNodeId: segment.end.id,
      behavior: structuredClone(segment.behavior),
      geometry: structuredClone(segment.geometry),
    });
    sectionIds.push(sectionId);
  }
  next.editor.pieceGroups = [
    {
      id: newStableId('piece'),
      definitionId: 'station',
      sectionIds,
      parameters: generated.geometry.parameters,
      definitionVersion: 2,
      entryPose: generated.geometry.entryPose,
      exitPose: generated.geometry.exitPose,
    },
  ];
  next.validation = createUnvalidatedValidationSummary();
  next.metadata.updatedAt = new Date().toISOString();
  return { ok: true, project: next, message: 'Station placed.' };
}

export function modularPieceLabel(id: ModularPieceDefinitionId): string {
  return {
    station: 'Station',
    straight: 'Straight',
    turn_left: 'Left Turn',
    turn_right: 'Right Turn',
    slope_up: 'Slope Up',
    slope_down: 'Slope Down',
    bank_left: 'Bank Left',
    bank_right: 'Bank Right',
    bank_level: 'Level Bank',
    lift: 'Lift',
    launch: 'Launch',
    brake: 'Brakes',
    photo: 'Photo',
    loop_left: 'Loop Left',
    loop_right: 'Loop Right',
    roll_left: 'Roll Left',
    roll_right: 'Roll Right',
    corkscrew_left: 'Corkscrew Left',
    corkscrew_right: 'Corkscrew Right',
  }[id];
}

function tangentFromGeometry(
  start: Vec3,
  geometry: ModularTrackSectionGeometry,
  end: Vec3,
  atEnd: boolean,
): Vec3 {
  return normalize(atEnd ? sub(end, geometry.control2) : sub(geometry.control1, start));
}

function angleDegrees(first: Vec3, second: Vec3): number {
  const dotValue = Math.min(
    1,
    Math.max(-1, first.x * second.x + first.y * second.y + first.z * second.z),
  );
  return (Math.acos(dotValue) * 180) / Math.PI;
}

export function auditModularPiece(geometry: ModularPieceGeometry): ModularPieceAuditRecord {
  let current = geometry.entryPose.position;
  let centerlineLengthMeters = 0;
  for (const segment of geometry.segments) {
    centerlineLengthMeters +=
      length(sub(segment.geometry.control1, current)) +
      length(sub(segment.geometry.control2, segment.geometry.control1)) +
      length(sub(segment.end.position, segment.geometry.control2));
    current = segment.end.position;
  }
  const first = geometry.segments[0]!;
  const last = geometry.segments.at(-1)!;
  const firstEnd = first.end.position;
  const lastStart =
    geometry.segments.length === 1
      ? geometry.entryPose.position
      : geometry.segments.at(-2)!.end.position;
  return {
    definitionId: geometry.definitionId,
    handedness: geometry.definitionId.endsWith('_left')
      ? 'left'
      : geometry.definitionId.endsWith('_right')
        ? 'right'
        : 'none',
    entryPose: geometry.entryPose,
    exitPose: geometry.exitPose,
    sectionCount: geometry.segments.length,
    centerlineLengthMeters: canonicalNumber(centerlineLengthMeters),
    elevationChangeMeters: canonicalNumber(
      geometry.exitPose.position.y - geometry.entryPose.position.y,
    ),
    headingChangeDegrees:
      (((geometry.exitPose.headingStep - geometry.entryPose.headingStep + 12) % 8) - 4) * 45,
    bankChangeDegrees: geometry.exitPose.bankDegrees - geometry.entryPose.bankDegrees,
    windingChangeDegrees:
      geometry.exitPose.rollWindingDegrees - geometry.entryPose.rollWindingDegrees,
    startTangentMismatchDegrees: angleDegrees(
      poseForward(geometry.entryPose),
      tangentFromGeometry(geometry.entryPose.position, first.geometry, firstEnd, false),
    ),
    endTangentMismatchDegrees: angleDegrees(
      poseForward(geometry.exitPose),
      tangentFromGeometry(lastStart, last.geometry, last.end.position, true),
    ),
  };
}
